// src/pages/landing/Home.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

const DURATION = 7000; // autoplay duration
const IMG_FADE_MS = 700; // image fade-in
const SUB_DELAY_MS = IMG_FADE_MS + 120; // subtitle starts after image is in

// Configure slides with multilingual subtitles
const slides = [
  {
    src: "/img/LandingPage/b2b/section1.webp",
    alt: "slide 1",
    subtitle: {
      zh: "安心潔淨  水禾開始",
      en: "Safe & Clean       With SEHO",
      jp: "安心と清潔。  SEHOから。",
      sx: {
        position: "absolute",
        left: { xs: "5%", sm: "6%", md: "8%", lg: "10%" },
        top: { xs: "60%", sm: "75%", md: "75%", lg: "75%" },
        maxWidth: { xs: "70%", sm: "65%", md: "55%", lg: "45%" },
        textAlign: "left",
      },
    },
  },
  {
    src: "/img/LandingPage/b2b/section2.webp",
    alt: "slide 2",
    subtitle: {
      zh: "食安專業  絕不妥協",
      en: " Food Safety      No Compromise",
      jp: "大切な食の安全、守り抜きます。",
      sx: {
        position: "absolute",
        right: { xs: "22%", sm: "30%", md: "30%", lg: "30%" },
        top: { xs: "30%", sm: "35%", md: "68%", lg: "75%" },
        maxWidth: { xs: "70%", sm: "64%", md: "52%", lg: "45%" },
        textAlign: "left",
      },
    },
  },
   {
    src: "/img/LandingPage/b2b/section5.webp",
    alt: "slide 1",
    subtitle: {
      zh: "專注領域  永續發展",
      en: "Focused  Sustainable",
      jp: "専門性を追求  持続可能な成長",
      sx: {
        position: "absolute",
        right: { xs: "5%", sm: "6%", md: "8%", lg: "5%" },
        top: { xs: "10%", sm: "10%", md: "10%", lg: "75%" },
        maxWidth: { xs: "70%", sm: "65%", md: "55%", lg: "45%" },
        textAlign: "left",
      },
    },
  },
  {
    src: "/img/LandingPage/b2b/section3.webp",
    alt: "slide 3",
    subtitle: {
      zh: "貼近生活  服務大眾",
      en: "Closer to Life  Served Daily",
      jp: "暮らし密着  みんなに奉仕",
      sx: {
        position: "absolute",
        left: { xs: "5%", sm: "5%", md: "7%", lg: "auto" },
        right: { xs: "auto", sm: "auto", md: "auto", lg: "60%" },
        top: { xs: "70%", sm: "68%", md: "72%", lg: "75%" },
        maxWidth: { xs: "70%", sm: "65%", md: "58%", lg: "60%" },
        textAlign: { xs: "left", sm: "left", md: "left", lg: "right" },
      },
    },
  },
  {
    src: "/img/LandingPage/b2b/section4.webp",
    alt: "slide 4",
    subtitle: {
      zh: "品質照顧  安全把關",
      en: "Clean Assurance   Safety Control",
      jp: "品質保証        安全管理",
      sx: {
        position: "absolute",
        right: { xs: "8%", sm: "10%", md: "12%", lg: "15%" },
        top: { xs: "15%", sm: "18%", md: "65%", lg: "75%" },
        maxWidth: { xs: "70%", sm: "64%", md: "52%", lg: "45%" },
        textAlign: "left",
      },
    },
  },
 
];

export default function HomeB2B({ bgcolor, fonts }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);
  const sectionRef = useRef(null);

  // language
  const [lang, setLang] = useState(() => getLangFromCookie() || "ZH");
  useEffect(() => {
    const handleLangChange = () => {
      const c = getLangFromCookie();
      setLang(c === "EN" || c === "JP" ? c : "ZH");
    };
    window.addEventListener("app:languageChanged", handleLangChange);
    return () =>
      window.removeEventListener("app:languageChanged", handleLangChange);
  }, []);

  // --- NEW: store natural aspect ratios and computed container height
  const [ratios, setRatios] = useState({}); // { src: naturalHeight / naturalWidth }
  const [containerH, setContainerH] = useState(0);

  const updateHeight = useCallback(
    (forcedRatio) => {
      const wrap = sectionRef.current;
      const w = wrap?.clientWidth || window.innerWidth;
      const activeSrc = slides[index].src;
      const r = forcedRatio ?? ratios[activeSrc] ?? 9 / 16; // sensible default
      setContainerH(Math.round(w * r));
    },
    [index, ratios],
  );

  // compute on mount / index change / resize
  useEffect(() => {
    updateHeight();
    const onResize = () => updateHeight();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updateHeight]);

  // called per-image load
  const handleImgLoad = (src, e) => {
    const { naturalWidth: nw, naturalHeight: nh } = e.target;
    if (!nw || !nh) return;
    const r = nh / nw;
    setRatios((prev) => {
      const next = { ...prev, [src]: r };
      return next;
    });
    // if this image is active, immediately use its ratio
    if (slides[index].src === src) updateHeight(r);
  };

  useEffect(() => {
    if (paused) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, DURATION);
    return () => clearInterval(timerRef.current);
  }, [paused]);

  const go = (dir) => {
    setIndex(
      (i) => (i + (dir === "next" ? 1 : -1) + slides.length) % slides.length,
    );
  };

  return (
    <Box
      ref={sectionRef}
      id="home"
      component="section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{
        position: "relative",
        width: "100%",
        height: containerH || "auto",
        overflow: "hidden",
        backgroundColor: bgcolor,
      }}
    >
      {/* Slides */}
      {slides.map((s, i) => {
        const active = i === index;
        const sub = s.subtitle;
        // pick translated line if EN / JP
        const translated =
          lang === "EN" ? sub.en : lang === "JP" ? sub.jp : null;

        return (
          <Box
            key={s.src}
            sx={{
              position: "absolute",
              inset: 0,
              opacity: active ? 1 : 0,
              transition: `opacity ${IMG_FADE_MS}ms ease`,
            }}
          >
            {/* Background image — NO CROP */}
            <Box
              component="img"
              src={s.src}
              alt={s.alt}
              onLoad={(e) => handleImgLoad(s.src, e)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain", // show entire image
                objectPosition: "center",
                display: "block",
                zIndex: 0,
              }}
            />

            {/* Overlay */}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                pointerEvents: "none",
                background:
                  "linear-gradient(90deg, rgba(0,0,0,.08), rgba(0,0,0,.08))",
                opacity: active ? 1 : 0,
                transition: `opacity ${IMG_FADE_MS}ms ease`,
              }}
            />

            {/* Subtitle block (ZH main + EN/JP sub) */}
            {sub?.zh && (
              <Box
                sx={{
                  ...sub.sx,
                  zIndex: 2,
                  color: "#fff",
                  fontFamily: fonts[lang],
                  textShadow:
                    "0 2px 6px rgba(0,0,0,.35), 0 6px 24px rgba(0,0,0,.25)",
                  transform: active ? "translateY(0px)" : "translateY(60px)",
                  opacity: active ? 1 : 0,
                  transition:
                    "opacity 900ms ease, transform 900ms cubic-bezier(.22,1,.36,1)",
                  transitionDelay: active ? `${SUB_DELAY_MS}ms` : "0ms",
                }}
              >
                {/* main ZH line */}
                <Box
                  component="div"
                  sx={{
                    whiteSpace: "pre-wrap",
                    fontFamily: fonts[lang],
                    fontWeight: 700,
                    lineHeight: { xs: 1.15, sm: 1.12, md: 1.1, lg: 1.1 },
                    letterSpacing: { xs: 1, sm: 1.25, md: 1.5, lg: 2 },
                    fontSize: { xs: 20, sm: 32, md: 44, lg: 64 },
                  }}
                >
                  {sub.zh}
                </Box>

                {/* translated line under it when lang is EN / JP */}
                {translated && (
                  <Box
                    component="div"
                    sx={{
                      mt: { xs: 0.5, sm: 0.75, md: 0.9, lg: 1 },
                      fontStyle: "italic",
                      whiteSpace: "pre-wrap",
                      fontFamily: fonts[lang],
                      fontWeight: 700,
                      lineHeight: { xs: 1.2, sm: 1.2, md: 1.15, lg: 1.1 },
                      fontSize: { xs: 12, sm: 18, md: 28, lg: 45 },
                      letterSpacing: { xs: 0.5, sm: 0.6, md: 0.8, lg: 1 },
                      opacity: 0.95,
                    }}
                  >
                    {translated}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {/* Arrows */}
      <IconButton
        aria-label="prev"
        onClick={() => go("prev")}
        sx={{
          position: "absolute",
          top: "50%",
          left: { xs: 8, sm: 10, md: 12, lg: 8 },
          width: { xs: 32, sm: 36, md: 40, lg: 40 },
          height: { xs: 32, sm: 36, md: 40, lg: 40 },
          p: 0,
          transform: "translateY(-50%)",
          bgcolor: "rgba(255,255,255,.8)",
          "&:hover": { bgcolor: "rgba(255,255,255,1)" },
          "& .MuiSvgIcon-root": {
            fontSize: { xs: 22, sm: 24, md: 26, lg: 24 },
          },
          zIndex: 3,
        }}
      >
        <ChevronLeftRounded />
      </IconButton>
      <IconButton
        aria-label="next"
        onClick={() => go("next")}
        sx={{
          position: "absolute",
          top: "50%",
          right: 8,
          transform: "translateY(-50%)",
          bgcolor: "rgba(255,255,255,.8)",
          "&:hover": { bgcolor: "rgba(255,255,255,1)" },
          zIndex: 3,
        }}
      >
        <ChevronRightRounded />
      </IconButton>

      {/* Dots */}
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          bottom: { xs: 8, sm: 10, md: 12, lg: 12 },
          transform: "translateX(-50%)",
          display: "flex",
          gap: { xs: 0.75, sm: 1, md: 1, lg: 1 },
          zIndex: 3,
        }}
      >
        {slides.map((_, i) => (
          <Box
            key={i}
            onClick={() => setIndex(i)}
            sx={{
              width: { xs: 7, sm: 8, md: 8, lg: 8, },
              height: { xs: 7, sm: 8, md: 8, lg: 8, },
              borderRadius: "50%",
              bgcolor: i === index ? "#ffffff" : "rgba(255,255,255,.45)",
              transform: i === index ? "scale(1.15)" : "scale(1)",
              cursor: "pointer",
              transition: "background-color .15s ease, transform .15s ease",
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
